/**
 * Header Menu Component
 * Dropdown menu for secondary header actions (Help, History, Feedback)
 */

import { useEffect } from 'react';
import { HelpCircle, History, Home, MessageSquare } from 'lucide-react';
import { useIsMobile } from '@/hooks';
import styles from './HeaderMenu.module.css';
import type { HeaderMenuProps } from './HeaderMenu.types';

export function HeaderMenu({
  isOpen,
  onClose,
  onGoHome,
  onShowShortcuts,
  onShowOrderHistory,
  onShowFeedback,
}: HeaderMenuProps) {
  // Close dropdown on Escape
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const isMobile = useIsMobile();

  if (!isOpen) return null;

  return (
    <div className={styles.dropdown} role="menu">
      <div className={styles.menuHeader}>
        <span className={styles.menuTitle}>Quick Actions</span>
      </div>

      {onGoHome && (
        <button
          type="button"
          className={styles.menuItem}
          onClick={() => {
            onClose();
            onGoHome();
          }}
          role="menuitem"
        >
          <Home size={18} className={styles.menuItemIcon} aria-hidden="true" />
          <span className={styles.menuItemLabel}>Change Product</span>
        </button>
      )}

      <button
        type="button"
        className={styles.menuItem}
        onClick={() => {
          onClose();
          onShowShortcuts?.();
        }}
        role="menuitem"
      >
        <HelpCircle size={18} className={styles.menuItemIcon} aria-hidden="true" />
        <span className={styles.menuItemLabel}>
          {isMobile ? 'Help & Tips' : 'Keyboard Shortcuts'}
        </span>
        {!isMobile && <span className={styles.menuItemShortcut}>?</span>}
      </button>

      <button
        type="button"
        className={styles.menuItem}
        onClick={() => {
          onClose();
          onShowOrderHistory?.();
        }}
        role="menuitem"
      >
        <History size={18} className={styles.menuItemIcon} aria-hidden="true" />
        <span className={styles.menuItemLabel}>Order History</span>
      </button>

      <button
        type="button"
        className={styles.menuItem}
        onClick={() => {
          onClose();
          onShowFeedback?.();
        }}
        role="menuitem"
      >
        <MessageSquare size={18} className={styles.menuItemIcon} aria-hidden="true" />
        <span className={styles.menuItemLabel}>Send Feedback</span>
      </button>
    </div>
  );
}
